import { MasterViewModel } from '../../../base-classes/master-view-model';
import { HttpClient } from '@angular/common/http';
import { signal, WritableSignal } from '@angular/core';
import { ConfirmationService } from 'primeng/api';
import { environment } from '../../../../environments/environment';
import { PackagesService } from '../services/packages.service';
import { Package, Client } from '../../test-suite/models';
import { TreeNode } from 'primeng/api';
import { FuntionalAreaService } from '../../test-suite/services/test-suite.service';
import { ScenarioService } from '../../scenario/services/scenario.service';
import { Table } from 'primeng/table';
import { ToastService } from 'src/app/toast/toast.service';
import { AuthService } from '../../../auth/login/services/auth.service';

export class PackagesViewModel extends MasterViewModel<Package> {
  packages = signal<Package[]>([]);
  allPackages = signal<Package[]>([]);
  clients = signal<Client[]>([]);
  clientOptions = signal<{ label: string; value: string }[]>([]);
  selectedClientFilter = signal<string>('');

  packageDialog = false;
  isEditMode = false;
  selectedPackage: Package | null = null;

  // Name dropdown: derived from existing distinct package names
  nameOptions = signal<{ label: string; value: string }[]>([]);

  constructor(
    http: HttpClient,
    private toast: ToastService,
    private confirmationService: ConfirmationService,
    private packagesService: PackagesService,
    private authService: AuthService,
    private funtionalAreaService?: FuntionalAreaService,
    private scenarioService?: ScenarioService
  ) {
    super(http, `${environment.apiUrl}/packages`, 'Packages');
    this.createForm({
      packageName: [null, [this.validators.required, this.validators.maxLength(255)]],
      packageDescription: [null, [this.validators.maxLength(500)]],
    });
  }

  isSystemUser(): boolean {
    const userInfo = this.authService.getUserInfoFromToken();
    return userInfo?.isSystemUser === true;
  }

  init(): void {
    this.loadPackages();
  }


  loadPackages(): void {
    this.packagesService.getAllPackages().subscribe({
      next: (packages) => {
        this.packages.set(packages || []);
      },
      error: () => {
        this.toast.error('Failed to load packages');
      },
    });
  }


  openNew(): void {
    this.selectedPackage = null;
    this.isEditMode = false;
    this.packageDialog = true;
    this.form.reset();
  }

  editPackage(pkg: Package): void {
    this.selectedPackage = pkg;
    this.isEditMode = true;
    this.packageDialog = true;
    this.form.patchValue({
      packageName: pkg.packageName,
      packageDescription: pkg.packageDescription || null,
    });
  }

  deletePackage(pkg: Package): void {
    this.confirmationService.confirm({
      message: `Are you sure you want to delete package ?`,
      header: 'Confirm',
      acceptLabel: 'Yes',
      rejectLabel: 'No',
      acceptButtonStyleClass: 'p-button-primary',
      rejectButtonStyleClass: 'p-button-warn',
      accept: () => {
        this.packagesService.deletePackage(pkg.id).subscribe({
          next: () => {
            this.toast.success('Package deleted Successfully');
            this.loadPackages();
          },
          error: (e) => this.toast.error(e?.error?.message || 'Delete failed'),
        });
      },
    });
  }

  savePackage(): void {
    if (!this.validateForm()) return;
    const val = this.form.getRawValue();
    const payload = {
      packageName: val.packageName,
      packageDescription: val.packageDescription,
    };

    if (this.isEditMode && this.selectedPackage) {
      this.packagesService.updatePackage(this.selectedPackage.id, payload).subscribe({
        next: () => {
          this.toast.success('Package updated successfully');
          this.hideDialog();
          this.loadPackages();
        },
        error: (e) => this.toast.error(e?.error?.message || 'Failed to update package'),
      });
    } else {
      this.packagesService.createPackage(payload).subscribe({
        next: () => {
          this.toast.success('Package created successfully');
          this.hideDialog();
          this.loadPackages();
        },
        error: (e) => this.toast.error(e?.error?.message || 'Failed to create package'),
      });
    }
  }

  hideDialog(): void {
    this.selectedPackage = null;
    this.isEditMode = false;
    this.packageDialog = false;
    this.form.reset();
    this.form.enable();
  }

  onGlobalFilter(table: Table, event: Event): void {
    table.filterGlobal((event.target as HTMLInputElement).value, 'contains');
  }

}
